// Higher or Lower: a chain of teams from the last finished week. Each team's
// fantasy points are shown, then the next team's result, and the player calls
// whether the next team scored higher or lower. One wrong call ends the game;
// ten right is a perfect game.
//
// The question shows only the result ("W 20-19"), not the opponent or the
// spread. Those come with the answer, along with how the points added up,
// because that is the skill the game is quietly teaching: a one point win as
// an underdog outscores a 23 point win that missed a big spread.
//
// Everything comes from data the app already holds: weekly points from the
// AuthContext teams map, and the week's games from fetchWeekGames.
import { explainTeamFantasyPoints } from "../utils/scoreBreakdown";
import { getTeamSpread } from "../utils/gameStatus";
import { latestScoredWeek, seasonThroughWeek } from "../utils/topTeams";
import { FIRST_SCORING_WEEK } from "../utils/season";
import { seededRandom, shuffle, squares } from "./daily";

export const GAME_ID = "higher-lower";
export const GAME_NAME = "Higher or Lower";
export const QUESTIONS = 10;

/**
 * One team's week, as the game shows it. Null when the team has no finished
 * game or no points for the week.
 */
export function describeTeamWeek(slug, teams, gamesBySlug, week) {
  const team = teams?.[slug];
  const game = gamesBySlug?.[slug];
  const points = team?.currentSeason?.weeklyPoints?.[week];
  if (!team || !game?.gameComplete || typeof points !== "number") return null;

  // buildGamesBySlug already turns each entry to this team's side: homeScore
  // is this team's score whether or not it was at home.
  const teamScore = game.homeScore;
  const oppScore = game.awayScore;
  const won = teamScore > oppScore;
  const spread = getTeamSpread(game, slug);
  const opponent = teams[game.awayTeam];

  // The breakdown is shown only when it adds up to the score of record. A
  // stat correction or a regraded line would otherwise show a sum that
  // disagrees with the number above it.
  const { lines, total } = explainTeamFantasyPoints({ won, spread, actualMargin: teamScore - oppScore });

  return {
    slug,
    name: team.name,
    logo: team.logo,
    points,
    won,
    result: `${won ? "W" : "L"} ${teamScore}-${oppScore}`,
    opponentName: opponent?.name || game.awayTeam,
    isHome: game.isHome,
    spread,
    lines: total === points ? lines : null,
  };
}

/** "Favoured by 25.5", "5.5 point underdog", or null with no line. */
export function describeSpread(spread) {
  if (typeof spread !== "number" || spread === 0) return spread === 0 ? "No favourite" : null;
  return spread < 0 ? `Favoured by ${Math.abs(spread)}` : `${spread} point underdog`;
}

/** Every FBS team with a finished game and points in `week`, by slug. */
function pool(teams, gamesBySlug, week) {
  return Object.keys(teams || {})
    .filter((slug) => teams[slug]?.classification === "fbs")
    .sort()
    .map((slug) => describeTeamWeek(slug, teams, gamesBySlug, week))
    .filter(Boolean);
}

/**
 * The chain for one seed: a starting team and ten more after it. Each next
 * team is the first in the day's order whose points differ from the team
 * before it, so a tie (which counts as right either way) is a rare gift
 * rather than every third question. Null if the week has too few teams.
 */
export function buildChain(teams, gamesBySlug, week, seed, length = QUESTIONS + 1) {
  const remaining = shuffle(pool(teams, gamesBySlug, week), seededRandom(seed));
  if (remaining.length < length) return null;
  const chain = [remaining.shift()];
  while (chain.length < length) {
    const last = chain[chain.length - 1].points;
    const i = remaining.findIndex((t) => t.points !== last);
    chain.push(remaining.splice(i === -1 ? 0 : i, 1)[0]);
  }
  return chain;
}

export const dailySeed = (day) => `${GAME_ID}:${day}`;
// The practice question is the same for everyone, whenever they first play.
export const PRACTICE_SEED = `${GAME_ID}:practice`;

/** Whether a call was right. Level points count as right either way. */
export function isRightCall(previous, next, call) {
  if (next === previous) return true;
  return call === "higher" ? next > previous : next < previous;
}

/** The text that goes to the share sheet. */
export function shareText(answers, dayLabel, week = null) {
  const score = answers.filter(Boolean).length;
  const trophy = score === QUESTIONS ? " 🏆" : "";
  // The week matters: the same team scores differently in another week, so
  // a score shared on a Wednesday has to say which week's results it was on.
  const weekNote = week === null ? "" : ` · Week ${week} results`;
  return `Lineup CFB ${GAME_NAME}${weekNote} · ${dayLabel}\n${squares(answers)} ${score}/${QUESTIONS}${trophy}`;
}

/**
 * The last finished week, the same rule the season table on /home uses, so a
 * Saturday's half-played week is never drawn on. Null before the first week
 * is final.
 */
export function gameWeek(teams, currentWeek) {
  return seasonThroughWeek(currentWeek, latestScoredWeek(teams));
}

/**
 * The week a game draws on: any finished week, from the first scoring week to
 * `through`, chosen from the seed so every device gets the same week on the
 * same day. Older weeks are as good as the latest one: a week's results never
 * change once it is final. The game names the week, so "Duke at UNC, week 3"
 * is a question about week 3. Null when no week is finished.
 */
export function pickWeek(through, seed) {
  if (through === null || through === undefined || through < FIRST_SCORING_WEEK) return null;
  const count = through - FIRST_SCORING_WEEK + 1;
  return FIRST_SCORING_WEEK + Math.floor(seededRandom(`${GAME_ID}:week:${seed}`)() * count);
}
